import React from 'react';
import { BadgeCheck, CalendarPlus, CheckCircle2, Clock } from 'lucide-react';
import { buildIcs, downloadIcs } from '../../lib/bookingSlots.js';
import AgentAvatar from './AgentAvatar.jsx';

/**
 * Link to the agent's Producer Page, or '' when the agent has not published one.
 * Built from the current origin so it works on any domain (local, staging, production).
 * @param {{producerPageSlug?: string}} page
 * @param {string} [origin]
 * @returns {string}
 */
export function producerPageHref(page, origin = typeof window !== 'undefined' ? window.location.origin : '') {
  const slug = String(page?.producerPageSlug || '').trim();
  return slug ? `${origin}/p/${encodeURIComponent(slug)}` : '';
}

/**
 * Branded confirmation: meeting details, agent avatar and an .ics invite download.
 * When the agent has a published Producer Page, a "verify who you're meeting" link sits at the bottom.
 * @param {{page: object, confirmed: {at: string}, brand: string, timeZone?: string}} props
 * @returns {JSX.Element}
 */
export default function BookingConfirmation({ page, confirmed, brand, timeZone }) {
  const agentName = page?.agent?.name || 'your agent';
  const minutes = page?.durationMinutes || 30;
  const when = new Date(confirmed.at).toLocaleString('en-US', { timeZone, timeZoneName: 'short', weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const producerHref = producerPageHref(page);
  const download = () => downloadIcs(
    buildIcs({ startAt: confirmed.at, minutes, title: `Appointment with ${agentName}`, description: page?.instructions || '' }),
    'appointment.ics',
  );
  return (
    <div className="text-center py-4" data-testid="booking-confirmation">
      <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: brand }} />
      <h2 className="text-2xl font-bold text-slate-900">You're booked!</h2>
      <div className="mt-5 mx-auto max-w-sm rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left">
        <div className="flex items-center gap-3">
          <AgentAvatar name={page?.agent?.name} avatar={page?.agent?.avatar} brand={brand} />
          <div>
            <div className="font-semibold text-slate-900">{agentName}</div>
            <span className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-0.5 bg-purple-100 text-purple-700">
              <Clock className="w-3 h-3" /> {minutes} min meeting
            </span>
          </div>
        </div>
        <div className="mt-3 text-sm text-slate-700">{when}</div>
      </div>
      <button type="button" onClick={download}
        className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-purple-500/25 hover:-translate-y-0.5 transition focus:outline-hidden focus-visible:ring-4 ring-purple-500/20"
        style={{ backgroundColor: brand }}>
        <CalendarPlus className="w-4 h-4" /> Add to calendar (.ics)
      </button>
      <p className="mt-3 text-xs text-slate-500">We'll send you a confirmation text shortly.</p>

      {producerHref && (
        <div className="mt-6 border-t border-slate-200 pt-4" data-testid="producer-page-link">
          <p className="text-xs text-slate-500">Want to know who you're meeting?</p>
          <a
            href={producerHref}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold underline underline-offset-2"
            style={{ color: brand }}
          >
            <BadgeCheck className="w-4 h-4" /> View {agentName}'s verified producer page
          </a>
        </div>
      )}
    </div>
  );
}
